import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getInspectionLifecycle,
  inspectionsKeys,
  transitionInspection,
  InspectionRevisionConflictSchema,
  TransitionInspectionBodySchema,
  type InspectionAvailableAction,
  type InspectionLifecycle,
  type TransitionInspectionBody,
} from '../../lib/api/inspections.gen';
import { ReassignInspectionDialog } from './ReassignInspectionDialog';
import { queryOrNull } from '../../lib/query/queryFn';
import { errorBody, errorMessage, errorReason } from '../../lib/errorMessage';
import { useCapabilityGate } from '../../lib/capabilities';
import {
  Callout,
  CardActions,
  Cluster,
  DataList,
  DialogActions,
  Form,
  FormField,
  FormSubmit,
  Modal,
  ModalBody,
  ModalHeader,
  StatusChip,
  StatusStepper,
  Textarea,
  ToolbarActions,
  useZodForm,
} from '../ui';
import type { StatusChipTone, StatusStepperStep } from '../ui';

// ═══════════════════════════════════════════════════════════════════════════
// The lifecycle affordance on the LEGACY detail surface (story 5-1a, phase 2)
// ═══════════════════════════════════════════════════════════════════════════
//
// What this replaces, and why it had to go. `/app/items/:id` shipped a single
// "Mark completed" button calling `POST /v1/inspections/:id/complete`. After
// 5-1a there is no `completed` state to mark — migration 012 maps it to
// `approved` — and, more importantly, one button cannot be truthful about a
// nine-state machine: it was enabled on rows the server would refuse, and it
// offered a verb that is not the next legal step from most states.
//
// What it is NOT. This is deliberately not a `StatusStepper`, not a schedule
// board and not a review queue: those surfaces are `MyDayPage`
// (scheduling), `ReviewQueuePage`/`ReviewDetailPage` (review) — the
// descendants of the stories originally named here (5-2a / 5-2b / 5-11a),
// now long landed. This is the minimum honest affordance that keeps the
// legacy page working through the compatibility window the Wave 5 header
// grants it (P17 Wave 5: `/app/items*` remains until the replacement
// surfaces pass parity).
//
// Story 2-146b re-investigated this note: none of those landed surfaces (nor
// template governance / report approval, this story's other two domains)
// renders an ordered, named-stage progression — every one of them offers a
// FLAT set of legal-next-moves (exactly this bar's own `offered`/`blocked`
// shape) rather than a sequence a person walks forward through. `StatusStepper`
// (story 2-141) accordingly still has no live consumer anywhere in this
// sweep — confirmed by grep, not by memory of this comment. That is not a
// gap this story owns to fill: inventing a stage list nobody's data models
// would be manufacturing a consumer to satisfy a primitive, backwards from
// how every other adoption in this lane works. Nothing currently owns
// building one; the next module that genuinely has an ordered-stage
// lifecycle is the one that adopts `StatusStepper`, same as `StatusChip`
// waited for its first four real consumers before this story converted them.
//
// ── Everything renderable comes from the server ────────────────────────────
// `GET /v1/inspections/:id/lifecycle` projects EVERY action with `allowed` and,
// when it is not, a `reason` sentence and the `requirement` that would satisfy
// it (`config/inspectionLifecycle.js → describeActions`). So this component
// holds no copy of the transition table and no idea of its own about what is
// legal — W4's "UI never offers illegal actions" is a property of rendering the
// projection, not of a rule duplicated here that could drift from it.
//
// Three renderings, one source:
//   * `allowed: true`                     → an enabled control.
//   * `allowed: false` + `requirement`
//     of `'reason'` / `'note'`            → an enabled control that opens a
//     FORM. The read-side projection passes `hasNote:false, hasReason:false`
//     by definition (nobody has typed anything yet), so `cancel` and
//     `request_changes` are ALWAYS refused on a read while their edge exists.
//     Rendering those as disabled would hide two legal transitions behind a
//     control that says "impossible" when it means "needs one field".
//   * anything else                       → a DISABLED control with the
//     server's sentence as VISIBLE TEXT beside it, never a tooltip alone (a
//     tooltip is not readable on touch and not announced on focus; the 4-4
//     precedent for a reasoned-disabled CTA renders a real text node too).
//
// ── Concurrency (P05 §7, W4 "a second reviewer cannot overwrite Rita") ─────
// `expectedRevision` is sent from the PROJECTION on every transition, never
// from a counter this component keeps: the number that travels is the number
// the server last told us, so a socket refresh or another tab's write means the
// next click carries the new revision rather than a stale local guess. On 409
// the refusal body carries the whole current projection (that is why
// `InspectionRevisionConflict.current` exists) — it is written straight into the
// query cache so the offered actions redraw immediately, AND refetched, so the
// user is never left looking at a screen the server disagrees with.
const CANCEL_REASON_SCHEMA = TransitionInspectionBodySchema.pick({ reason: true }).required();
const REQUEST_CHANGES_NOTE_SCHEMA = TransitionInspectionBodySchema.pick({ note: true }).required();

type TransitionAction = InspectionAvailableAction['action'];
export type InspectionState = InspectionLifecycle['status'];

const ACTION_LABEL: Record<TransitionAction, string> = {
  schedule: 'Schedule',
  assign: 'Assign',
  start: 'Start work',
  submit: 'Submit for review',
  review: 'Open review',
  request_changes: 'Request changes',
  approve: 'Approve',
  deliver: 'Mark delivered',
  cancel: 'Cancel inspection',
};

// Exported (story 2-141): `ReviewDetailPage` used to hand-roll its own
// two-tone (`inReview ? 'primary' : 'neutral'`), unlabelled ternary for this
// exact same status value — the review page's own status IS an
// `InspectionLifecycle['status']`, not a second vocabulary. Reusing this map
// rather than writing a third near-duplicate one is the P18 §6 "rule of
// three" discipline this lane already follows for primitives; it applies
// just as much to a label/tone map as to a component.
//
// This is also the source of the 3 remaining `react-refresh/only-export-
// components` warnings on `STATE_LABEL`/`STATE_TONE`/`STATE_WEIGHT` below —
// deliberately left in place (2-147wc): that rule is about Vite dev-server
// Fast Refresh performance, a DIFFERENT axis from every `design-language/*`
// rule this story's own mandate targets, and NOT a design violation. Moving
// these three maps to a new file to silence it would be a real, live-risk
// refactor of a shared export three other modules already import from this
// exact path (`ReviewDetailPage.tsx`, `ReviewQueuePage.tsx`) for zero user-
// facing benefit — out of scope for a story whose own rules forbid touching
// query keys/contracts and require every changed route to keep its exact
// e2e assertions; a needless import-path churn is the wrong place to spend
// that risk budget.
export const STATE_LABEL: Record<InspectionState, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  assigned: 'Assigned',
  in_progress: 'In progress',
  submitted: 'Submitted',
  in_review: 'In review',
  approved: 'Approved',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  // Legacy, read-tolerated for one wave (DOD §1.1) — migration 012 maps it.
  completed: 'Approved',
};

// Story 2-141's real fix: every non-terminal state used to collapse onto the
// SAME `primary` Badge tone (`lifecycle.terminal ? 'neutral' : 'primary'`),
// so "assigned", "in_progress", "submitted" and "in_review" were visually
// identical despite being four different places in the machine. Each state
// now gets the tone that matches its own FAMILY — not-yet-started, active
// work, awaiting somebody else, or a resolved outcome — which is
// `StatusChip`'s whole closed vocabulary (five values); a nine/ten-state
// machine mapped onto five tones necessarily puts more than one state per
// family, e.g. `assigned`/`in_progress` both read `primary`.
//
// `STATE_WEIGHT` is what keeps every state pairwise distinguishable even so
// (done-when 2's grayscale test): within each tone family, the SECOND state
// (chronologically later) is `bold`, so two same-tone neighbours differ by a
// real rendered property — solid fill vs. tinted text — not only by their
// text label. This replaces the earlier `lifecycle.terminal ? 'bold' :
// 'subtle'` rule (which only distinguished the terminal pair, and did
// nothing for `assigned` vs `in_progress` or `submitted` vs `in_review`).
// `completed` is deliberately the ONE exception: STATE_LABEL already renders
// it with the IDENTICAL text "Approved" (a legacy value migration 012 maps
// onto `approved`'s meaning), so it is meant to be visually identical to
// `approved`, not disambiguated from it.
export const STATE_TONE: Record<InspectionState, StatusChipTone> = {
  draft: 'neutral',
  scheduled: 'neutral',
  assigned: 'primary',
  in_progress: 'primary',
  submitted: 'warning',
  in_review: 'warning',
  approved: 'success',
  delivered: 'success',
  cancelled: 'destructive',
  // Legacy, read-tolerated (see STATE_LABEL above) — same tone as approved,
  // the state migration 012 maps it to.
  completed: 'success',
};

export const STATE_WEIGHT: Record<InspectionState, 'subtle' | 'bold'> = {
  draft: 'subtle',
  scheduled: 'bold',
  assigned: 'subtle',
  in_progress: 'bold',
  submitted: 'subtle',
  in_review: 'bold',
  approved: 'subtle',
  delivered: 'bold',
  cancelled: 'subtle',
  completed: 'subtle',
};

// Story 2-147k, `capture` variant only: the ordered, non-terminal sequence
// `StatusStepper` needs — mirrors `backend/src/config/inspectionLifecycle.js`
// own comment (line 8) verbatim: "draft → scheduled → assigned →
// in_progress → submitted → in_review → approved → delivered". `cancelled`
// is deliberately absent — that file's own `TERMINAL_STATUSES` list treats
// it as an EXIT from any non-terminal state, not a further step in this
// sequence, so a cancelled inspection renders the existing StatusChip row
// instead of the stepper (see the render branch below).
const LIFECYCLE_SEQUENCE: readonly InspectionState[] = [
  'draft',
  'scheduled',
  'assigned',
  'in_progress',
  'submitted',
  'in_review',
  'approved',
  'delivered',
];

function sequenceSteps(status: InspectionState): StatusStepperStep[] {
  // `completed` is the legacy alias STATE_LABEL already renders identically
  // to `approved` (migration 012's own mapping) — normalized here so it
  // lands on the same step rather than matching nothing.
  const normalized = status === 'completed' ? 'approved' : status;
  const currentIndex = LIFECYCLE_SEQUENCE.indexOf(normalized);
  return LIFECYCLE_SEQUENCE.map((step, i) => ({
    id: step,
    label: STATE_LABEL[step],
    state: i < currentIndex ? 'complete' : i === currentIndex ? 'current' : 'upcoming',
  }));
}

const DESTRUCTIVE: ReadonlySet<TransitionAction> = new Set<TransitionAction>(['cancel', 'request_changes']);

/** An action refused only because the caller has not supplied prose yet. */
function promptFor(action: InspectionAvailableAction): 'reason' | 'note' | null {
  if (action.allowed) return null;
  // `assign` is deliberately excluded (story 5-2a). It is also refused for a
  // missing `reason` once the row is already assigned — but the input it needs
  // is a PERSON as well, resolved from a server-filtered endpoint, and a
  // textarea cannot ask that. It gets `ReassignInspectionDialog` instead; see
  // `needsAssignDialog` below.
  if (action.action === 'assign') return null;
  if (action.requirement === 'reason' || action.requirement === 'note') return action.requirement;
  return null;
}

/**
 * The assign edge, in every shape the lifecycle table can hand it back (story
 * 5-2a): allowed outright, blocked for want of an assignee (`draft`/`scheduled`),
 * or blocked for want of a reason (an already-`assigned` row changing hands).
 * All three open the same dialog, because all three are one question — "who, and
 * why" — and the dialog asks for both at once rather than making the user
 * discover the second requirement by being refused.
 *
 * What this does NOT match is an assign that is illegal by TOPOLOGY (an
 * `in_progress` row): `requirement` is null there, so it falls through to the
 * disabled list with the server's sentence, exactly like every other dead edge.
 */
function needsAssignDialog(action: InspectionAvailableAction): boolean {
  if (action.action !== 'assign') return false;
  return action.allowed || action.requirement === 'assignee' || action.requirement === 'reason';
}

interface Feedback {
  tone: 'warning' | 'danger';
  title: string;
  message: string;
}

// ── The two prose forms (G-26) ─────────────────────────────────────────────
// Both schemas are the GENERATED contract schema narrowed with `.pick()` +
// `.required()` — never a `z.object({...})` written next to the form. `.pick()`
// keeps `min(1)/max(4000)` exactly as the server enforces them; `.required()`
// turns the wire-optional field into a form-required one, which is the whole
// difference between the request shape (where `reason` is optional because most
// actions do not take one) and THIS request (where the lifecycle table refuses
// without it — `transition_blocked`, `requirement:'reason'`).

function CancelReasonForm({
  pending,
  onSubmit,
}: {
  pending: boolean;
  onSubmit: (reason: string) => void;
}): JSX.Element {
  const form = useZodForm(CANCEL_REASON_SCHEMA, { defaultValues: { reason: '' } });
  return (
    <Form form={form} onSubmit={(values) => onSubmit(values.reason)} className="space-y-4">
      <FormField
        name="reason"
        label="Why is this inspection being cancelled?"
        description="Cancelling is final — this reason is the only record of why the work stopped."
        required
      >
        {(field) => <Textarea {...field} rows={4} maxLength={4000} data-testid="cancel-reason-input" />}
      </FormField>
      <FormSubmit variant="destructive" disabled={pending} data-testid="cancel-reason-submit">
        Cancel inspection
      </FormSubmit>
    </Form>
  );
}

function RequestChangesNoteForm({
  pending,
  onSubmit,
}: {
  pending: boolean;
  onSubmit: (note: string) => void;
}): JSX.Element {
  const form = useZodForm(REQUEST_CHANGES_NOTE_SCHEMA, { defaultValues: { note: '' } });
  return (
    <Form form={form} onSubmit={(values) => onSubmit(values.note)} className="space-y-4">
      <FormField
        name="note"
        label="What needs to change?"
        description="The assignee sees this note when the work comes back to them."
        required
      >
        {(field) => <Textarea {...field} rows={4} maxLength={4000} data-testid="request-changes-note-input" />}
      </FormField>
      <FormSubmit variant="destructive" disabled={pending} data-testid="request-changes-note-submit">
        Send back for changes
      </FormSubmit>
    </Form>
  );
}

// The blocked-action list's own content — identical for both variants, see
// the render-site comment above for why this never gets simplified away.
// Only the CHROME around it differs: `detail` renders it open, `capture`
// wraps it in a collapsed-by-default `<details>` so it stops dominating a
// form-filling screen while staying one click away, never deleted.
function BlockedActionsContent({
  blockedReasonsShared,
  blocked,
}: {
  blockedReasonsShared: boolean;
  blocked: InspectionAvailableAction[];
}): JSX.Element {
  return blockedReasonsShared ? (
    <Callout tone="info" title="Not possible from here" data-testid="lifecycle-blocked">
      {/* Same server sentence every blocked action shares in this terminal
          state — one real text node, not a tooltip, matching the per-action
          rendering's own contract below. */}
      <span data-testid="transition-reason-shared">{blocked[0]!.reason}</span>
    </Callout>
  ) : (
    <div className="space-y-2" data-testid="lifecycle-blocked">
      <p className="text-xs uppercase tracking-eyebrow text-muted-foreground">Not possible from here</p>
      {blocked.map((action) => (
        <Cluster key={action.action} align="start" gap={2}>
          <CardActions
            align="start"
            actions={[
              {
                id: action.action,
                label: ACTION_LABEL[action.action],
                variant: 'secondary',
                disabled: true,
                'data-testid': `transition-${action.action}`,
              },
            ]}
          />
          {/* The server's own sentence, as a real text node — not a tooltip. */}
          <p data-testid={`transition-reason-${action.action}`} className="max-w-prose flex-1 text-xs text-muted-foreground">
            {action.reason}
          </p>
        </Cluster>
      ))}
    </div>
  );
}

// Story 2-147wc, direct screenshotted user feedback on `ReviewDetailPage`
// (the `'detail'`-variant consumer): six always-open disabled pills, each
// paired with a full explanatory sentence, consumed roughly a third of the
// page's visible height for information almost nobody reads on every visit.
// `story 2-147k` already solved this exact problem for the `capture`
// variant (a collapsed-by-default `<details>` disclosure) — this was never
// widened to `detail` only because, at the time, `detail` was still
// describing the (since-retired, story 5-12) legacy `/app/items/:id` page,
// not `ReviewDetailPage`. Re-confirmed before widening: no e2e spec anywhere
// asserts this content is visible WITHOUT first opening a disclosure (grepped
// `lifecycle-blocked`/`transition-reason-` tree-wide — the only hits are
// `story-5-1a.spec.mjs` check 1, which is `capture`-routed already, per
// `openItem`'s own routing table). Collapsed unconditionally now, not behind
// a second `isCapture`-only branch — there is no real consumer left that
// wants the always-open shape, and inventing a THIRD prop to keep a dead
// branch alive would be exactly the speculative flexibility P18 §6 declines.
function BlockedActions({
  blockedReasonsShared,
  blocked,
}: {
  blockedReasonsShared: boolean;
  blocked: InspectionAvailableAction[];
}): JSX.Element {
  return (
    <details className="group" data-testid="lifecycle-blocked-disclosure">
      <summary className="focus-ring cursor-pointer select-none rounded text-xs uppercase tracking-eyebrow text-muted-foreground">
        Why can&apos;t I do more from here?
      </summary>
      <div className="mt-2">
        <BlockedActionsContent blockedReasonsShared={blockedReasonsShared} blocked={blocked} />
      </div>
    </details>
  );
}

export interface InspectionLifecycleBarProps {
  inspectionId: string;
  /** Called after a successful transition so the page can refresh its own reads. */
  onTransitioned?: () => void;
  /**
   * `'detail'` (default): the original story-5-1a rendering — a flat legal-
   * next-moves list (status chip, offered actions, every blocked action with
   * its reason). `story-5-1a.spec.mjs`'s concurrency/prose-requirement
   * checks exercise this shape through `ReviewDetailPage` (the one real
   * `'detail'`-variant consumer today — the legacy `/app/items/:id` page
   * this branch originally described was retired by story 5-12).
   * `'capture'` (story 2-147k, direct user feedback: "crowded and not very
   * intuitive"): `CapturePage`'s inspector panel. Swaps the blocked-action
   * wall for a `StatusStepper` ordered-sequence view (the flat list's own
   * job — "why can't I do X" — is better answered by "here is where you
   * are" for a form-filling surface, not a review/admin one), and drops
   * `submit` from the offered-actions row because `SubmitBar` already
   * renders that exact verb in the canvas below — rendering both was a
   * live, screenshot-confirmed literal duplicate button.
   */
  variant?: 'detail' | 'capture';
  /** `capture` variant only: the pinned template's name, for the context
   * block (`CapturePage` already holds this from its own answers query —
   * threaded through rather than re-fetched here). */
  templateName?: string | null;
  /**
   * Story 2-147wc, direct screenshotted user feedback: `ReviewDetailPage`
   * excludes whichever of `review`/`approve`/`request_changes` its own
   * bespoke header controls (`open-review`/`approve`/`request-changes`)
   * ALREADY cover at the CURRENT status — passed as a plain array, computed
   * by the caller from its own `status`/`inReview` state, not a static
   * constant, because `request_changes`'s legal window
   * (`backend/src/config/inspectionLifecycle.js`'s own `TOPOLOGY.
   * request_changes: ['submitted', 'in_review']`) opens one state BEFORE the
   * header's own `inReview`-gated button appears — while `submitted`, the
   * bar is the ONLY control that reaches it, so the caller leaves it out of
   * the exclusion list there and only adds it once `inReview` makes the
   * header's own button cover the same edge. `review`
   * (`TOPOLOGY.review: ['submitted']`) and `approve`
   * (`TOPOLOGY.approve: ['in_review']`) have no such earlier window — their
   * topology matches the header's own gate exactly at every status, so the
   * caller excludes both unconditionally. Generalizes the `capture`
   * variant's existing "drop `submit`" special case (above) into a
   * caller-supplied list, rather than hard-coding a second one-off branch.
   * Applies to the offered-actions row only — the status chip and blocked-
   * action list (P05 §7's "never offers illegal actions... every blocked
   * edge must still be EXPLAINED") are unaffected, and an excluded action
   * that later becomes BLOCKED still renders in the blocked list with its
   * server reason, exactly like any other action.
   */
  excludeActions?: TransitionAction[];
  /**
   * Story 2-147wc, direct user feedback ("still see duplication of status
   * and attempt in header and in the lifecycle bar"): `ReviewDetailPage`'s
   * own `PageHeader` already carries a `StatusChip` (`review-status`
   * testid) and the attempt count in its `chips` slot — this bar's own
   * status/revision row said the identical status a second time directly
   * underneath it. Defaults to `true` (byte-identical for `capture`, whose
   * `WorkspaceHeader` carries no status chip of its own — this is the ONLY
   * status affordance on that page, `story-5-12`/`2-146c` assert
   * `lifecycle-status` there directly). `false` on `ReviewDetailPage` drops
   * the row entirely rather than hiding it visually — no dead DOM, no
   * `lifecycle-status`/`lifecycle-revision` testid left behind for a test to
   * accidentally still find; that page's own spec assertions were
   * repointed at its real `review-status` header chip instead (see
   * `story-5-1a.spec.mjs` check 6's own comment). Blocked-action list and
   * offered-action row are UNAFFECTED — a reviewer can still see and act on
   * every legal/blocked edge, only the redundant status repetition is gone.
   */
  showStatus?: boolean;
}

export function InspectionLifecycleBar({
  inspectionId,
  onTransitioned,
  variant = 'detail',
  templateName = null,
  excludeActions = [],
  showStatus = true,
}: InspectionLifecycleBarProps): JSX.Element | null {
  const queryClient = useQueryClient();
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [prompt, setPrompt] = useState<InspectionAvailableAction | null>(null);
  // The assign edge's own dialog (story 5-2a) — see `needsAssignDialog`.
  const [assigning, setAssigning] = useState(false);

  // G-13: the flag half of the gate, read once, for whether to render the bar at
  // all. It is ADVISORY — `requireFeature('scheduling.core')` on both routes is
  // what enforces it, and a 403 from there still surfaces below with the
  // server's own sentence (G-27). No `permission` argument: this bar offers
  // several actions guarded by four DIFFERENT capabilities
  // (`inspections:create` / `:assign` / `:review` / `:complete`), so a single
  // permission string here would be a lie about at least three of them.
  const gate = useCapabilityGate('scheduling.core');

  // G-14 / G-19: the key comes from the generated module factory, the queryFn
  // from the shared `queryOrNull` helper. `inspectionsKeys.detail(id)` is the
  // inspections module's own detail key and is unused elsewhere (the legacy
  // record read on this page still uses its pre-contract `['inspection', id]`).
  const {
    data: lifecycle,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: inspectionsKeys.detail(inspectionId),
    queryFn: async () => (await queryOrNull(getInspectionLifecycle(inspectionId)))?.data ?? null,
    enabled: Boolean(inspectionId) && gate.isHydrated && gate.allowed,
  });

  const mutation = useMutation({
    mutationFn: (body: TransitionInspectionBody) => transitionInspection(inspectionId, body),
    onSuccess: async (response) => {
      setFeedback(null);
      setPrompt(null);
      queryClient.setQueryData(inspectionsKeys.detail(inspectionId), response.data);
      onTransitioned?.();
      await refetch();
    },
    onError: async (err: unknown) => {
      const reason = errorReason(err);
      if (reason === 'revision_conflict') {
        // The refusal carries the whole current projection, so redraw from it
        // immediately (no second round trip) — then refetch to confirm.
        const parsed = InspectionRevisionConflictSchema.safeParse(errorBody(err));
        if (parsed.success) {
          queryClient.setQueryData(inspectionsKeys.detail(inspectionId), parsed.data.current);
        }
        // Story 2-146b: this used to close the prompt unconditionally
        // (`setPrompt(null)`), which for `cancel`/`request_changes` — the
        // two actions that open a FORM (`CancelReasonForm`/
        // `RequestChangesNoteForm`) — discarded whatever reason/note the
        // reviewer had already typed. The modal now stays open (RHF's local
        // state is untouched, since the form never unmounts) so that text is
        // still there; only the lifecycle CACHE and the surrounding feedback
        // update. A retry submit reads `lifecycle.revision` fresh off the
        // just-updated query, so it quotes the CURRENT revision rather than
        // repeating the same losing one.
        setFeedback({
          tone: 'warning',
          title: 'This inspection moved while you had it open',
          message: prompt
            ? `${errorMessage(err)} Nothing you typed here is lost — review it below and send again.`
            : errorMessage(err),
        });
        await refetch();
        return;
      }
      if (reason === 'illegal_transition' || reason === 'transition_blocked') {
        // The 422 also carries the legal set; refetching the projection is how
        // the offered controls correct themselves against it.
        setFeedback({ tone: 'danger', title: 'That step is not possible right now', message: errorMessage(err) });
        await refetch();
        return;
      }
      setFeedback({ tone: 'danger', title: 'That change could not be saved', message: errorMessage(err) });
    },
  });

  const send = (action: InspectionAvailableAction, extra?: { note?: string; reason?: string }) => {
    if (!lifecycle) return;
    mutation.mutate({
      to: action.to,
      action: action.action,
      expectedRevision: lifecycle.revision,
      ...extra,
    });
  };

  const activate = (action: InspectionAvailableAction) => {
    setFeedback(null);
    if (needsAssignDialog(action)) {
      setAssigning(true);
      return;
    }
    if (promptFor(action)) {
      setPrompt(action);
      return;
    }
    send(action);
  };

  // The flag is off / not entitled: render nothing rather than a control the
  // route would refuse. `PlanTeaser` belongs to the surfaces that own the
  // feature (5-2a/5-2b), not to a legacy detail header.
  if (gate.isHydrated && !gate.allowed) return null;
  if (isLoading || (!lifecycle && !error)) {
    return <div data-testid="lifecycle-loading" className="h-9 w-48 animate-pulse rounded-lg bg-muted" />;
  }

  if (!lifecycle) {
    return (
      <Callout tone="danger" title="The lifecycle of this inspection could not be read" data-testid="lifecycle-error">
        {errorMessage(error)}
      </Callout>
    );
  }

  const offered = lifecycle.actions.filter(
    (action) => action.allowed || promptFor(action) !== null || needsAssignDialog(action)
  );
  const blocked = lifecycle.actions.filter(
    (action) => !action.allowed && promptFor(action) === null && !needsAssignDialog(action)
  );
  const pending = mutation.isPending;

  // Direct user feedback (live, "crowded and not intuitive"): a TERMINAL
  // state (cancelled/approved/delivered/completed) blocks EVERY action, and
  // the server's own `illegalSentence()` (backend/src/config/
  // inspectionLifecycle.js) returns the byte-identical sentence for all of
  // them in that case — it only varies by action for a non-terminal state,
  // where each action genuinely has its own "possible from X" reason (see
  // that function's own branches). Rendering up to 9 disabled buttons each
  // paired with the SAME repeated sentence ("This inspection was cancelled.
  // Cancelled work is not reopened.", nine times) is real redundancy, not
  // completeness — confirmed by reading the server's own reason function,
  // not guessed. `docs/design/sitewise-ux-reference.html`'s own lifecycle
  // primitive states this compactly ("Legal next state · reason ·
  // permission · audit" — one line, not one row per hypothetical action).
  // Collapsed ONLY when there is real duplication to remove (>1 blocked
  // action, all sharing one reason) — the non-terminal case (genuinely
  // different reasons per action, `story-5-1a.spec.mjs` check 1's own
  // frozen contract) is completely unaffected; every per-action button/
  // reason testid stays exactly as before there.
  const blockedReasonsShared = blocked.length > 1 && new Set(blocked.map((a) => a.reason)).size === 1;

  // Story 2-147k, `capture` variant only — direct user feedback ("this Ux of
  // Section Nav and lifecycleBar is not right, it looks crowded"): `submit`
  // is dropped from the rendered offered-actions row because `SubmitBar`
  // (CapturePage's own richer, idempotent, issue-navigable submit control)
  // already renders that exact verb in the canvas below — live screenshots
  // confirmed both buttons rendering at once, a literal duplicate. `offered`
  // itself is untouched (the `detail` variant, and the mutation logic both
  // variants share, still see the real, complete set).
  const isCapture = variant === 'capture';
  // `submit` (capture-only) and the caller's own `excludeActions` (story
  // 2-147wc, see the prop's own doc comment) are two independently-reasoned
  // filters over the same real, complete `offered` set — `offered` itself
  // stays untouched either way (the mutation logic both variants share
  // still sees it).
  const renderedOffered = offered.filter((action) => {
    if (isCapture && action.action === 'submit') return false;
    if (excludeActions.includes(action.action)) return false;
    return true;
  });

  // `capture` variant only: real, already-available fields threaded from the
  // caller (`templateName`) or the projection itself (`dueAt`) — never a
  // fabricated "Assigned to" name, since neither this query nor CapturePage's
  // own has one to resolve `lifecycle.assignedTo`'s raw id against today.
  const contextItems: { label: string; value: string }[] = [];
  if (isCapture && templateName) {
    contextItems.push({ label: 'Template', value: `${templateName} · pinned` });
  }
  if (isCapture && lifecycle.dueAt) {
    contextItems.push({
      label: 'Due',
      value: new Date(lifecycle.dueAt).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }),
    });
  }

  return (
    <div data-testid="lifecycle-bar" className="w-full space-y-3">
      {showStatus && (isCapture && lifecycle.status !== 'cancelled' ? (
        // Ordered-sequence view (StatusStepper, story 2-141's first real
        // consumer) replaces the status chip row here: for a form-filling
        // surface, "here is where you are in the sequence" answers the
        // question a status word alone does not. `cancelled` is a terminal
        // EXIT from the sequence (backend's own `TERMINAL_STATUSES`), not a
        // further step in it, so it falls through to the unchanged chip row
        // below instead of a stepper with nowhere sensible to put it.
        // `layout="vertical"` (not `horizontal`): direct user feedback,
        // live — an 8-step horizontal sequence in a fixed 22rem panel had
        // to scroll to see anything past the first three steps, when the
        // panel column itself has plenty of UNUSED vertical height below it.
        // Matches `docs/design/sitewise-ux-reference.html`'s own
        // `.swa-version-panel` dot-and-continuous-line shape (a chronology/
        // history-list pattern that file already establishes but no React
        // component had implemented before this).
        <div className="space-y-2">
          {/* `lifecycle-status`/`data-status` stay real DOM (story-5-12/
              2-146c both assert `toHaveAttribute` on capture directly) —
              `sr-only` because the stepper's own "current" step already
              says this visually; `aria-live` keeps a transition announced
              to assistive tech even though the text itself is hidden. */}
          <div aria-live="polite" className="sr-only">
            <span data-testid="lifecycle-status" data-status={lifecycle.status}>
              {STATE_LABEL[lifecycle.status]}
            </span>
          </div>
          <StatusStepper
            aria-label="Inspection lifecycle"
            data-testid="lifecycle-stepper"
            layout="vertical"
            steps={sequenceSteps(lifecycle.status)}
          />
          <span data-testid="lifecycle-revision" className="text-xs text-muted-foreground">
            {`revision ${lifecycle.revision} · attempt ${lifecycle.attempt}`}
          </span>
        </div>
      ) : (
        // `aria-live` needs a real DOM attribute none of `Layout.tsx`'s typed
        // layout primitives expose on their own props surface — wrapped
        // around `Cluster` (which now owns the actual `flex` row) rather than
        // left as a second raw-flex className, so the live-region contract
        // and the primitive-composition rule both hold (2-147wc).
        <div aria-live="polite">
          <Cluster gap={3}>
            <span className="text-xs uppercase tracking-eyebrow text-muted-foreground">Status</span>
            {/* FROZEN contract (story-5-1a.spec.mjs, do not rename): the
                `lifecycle-status` testid and `data-status` attribute stay on
                THIS span exactly as before — only the inner Badge became a
                StatusChip. `aria-live="polite"` on the row above announces a
                transition (P05 §10/P10 §2.3's connectivity/agent-status
                discipline extended to workflow state, story 2-141). */}
            <span data-testid="lifecycle-status" data-status={lifecycle.status}>
              <StatusChip
                tone={STATE_TONE[lifecycle.status]}
                weight={STATE_WEIGHT[lifecycle.status]}
                label={STATE_LABEL[lifecycle.status]}
                value={lifecycle.status}
              />
            </span>
            <span data-testid="lifecycle-revision" className="text-xs text-muted-foreground">
              {`revision ${lifecycle.revision} · attempt ${lifecycle.attempt}`}
            </span>
          </Cluster>
        </div>
      ))}

      {lifecycle.degradations.map((degradation) => (
        <Callout
          key={degradation.code}
          tone="warning"
          title={degradation.code === 'type_inactive' ? 'This inspection type is switched off' : 'The assignee is gone'}
          data-testid={`lifecycle-degradation-${degradation.code}`}
        >
          {degradation.message}
        </Callout>
      ))}

      {feedback && (
        <Callout tone={feedback.tone} title={feedback.title} data-testid="transition-feedback">
          {feedback.message}
        </Callout>
      )}

      {contextItems.length > 0 && (
        <DataList density="compact" items={contextItems} data-testid="lifecycle-context" />
      )}

      {renderedOffered.length > 0 && (
        // `maxVisible={renderedOffered.length}` — never lets `ToolbarActions`
        // (story 2-147r) collapse any action into its own `OverflowMenu`:
        // `story-5-1a.spec.mjs`'s `offeredActions()` helper reads every
        // `<button>` under this testid directly, so an overflowed action
        // (rendered as a menu item, not a `<button>`) would silently vanish
        // from that read. The original always rendered every offered action
        // inline with no overflow behavior at all — this preserves that
        // exactly (2-147wc).
        <ToolbarActions
          size="sm"
          maxVisible={renderedOffered.length}
          data-testid="lifecycle-actions"
          actions={renderedOffered.map((action) => ({
            id: action.action,
            label:
              needsAssignDialog(action) && lifecycle.assignedTo
                ? 'Reassign…'
                : promptFor(action) || needsAssignDialog(action)
                  ? `${ACTION_LABEL[action.action]}…`
                  : ACTION_LABEL[action.action],
            variant: DESTRUCTIVE.has(action.action) ? 'destructive' : 'primary',
            disabled: pending,
            onClick: () => activate(action),
            'data-testid': `transition-${action.action}`,
          }))}
        />
      )}

      {/* P05 §7/W4: "UI never offers illegal actions" carries a matching
          completeness rule — every blocked edge must still be EXPLAINED as
          real text, never just hidden, or a client would look like it has
          fewer options than it really does. Story 2-147k's own stepper (for
          `capture`) answers "where am I in the sequence", a real but
          DIFFERENT question from "why can't I do X specifically" — it does
          not make the per-action reasons redundant, so every one of them is
          kept, exactly as `story-5-1a.spec.mjs` check 1 (capture-routed —
          `openItem`'s own routing sends every non-terminal status to
          `/app/inspections/:id/capture`) still verifies. Default visibility
          is collapsed behind a native `<details>` disclosure for BOTH
          variants (story 2-147wc widened this from `capture`-only — see
          `BlockedActions`'s own header comment for why). */}
      {blocked.length > 0 && (
        <BlockedActions blockedReasonsShared={blockedReasonsShared} blocked={blocked} />
      )}

      {assigning && (
        <ReassignInspectionDialog
          inspectionId={inspectionId}
          lifecycle={lifecycle}
          onClose={() => setAssigning(false)}
          onAssigned={(next) => {
            queryClient.setQueryData(inspectionsKeys.detail(inspectionId), next);
            onTransitioned?.();
          }}
        />
      )}

      {prompt && (
        <Modal onClose={() => setPrompt(null)} label={ACTION_LABEL[prompt.action]}>
          {/* No ModalFooter here: the primary action is `FormSubmit`, owned by
              `CancelReasonForm`/`RequestChangesNoteForm`'s own self-contained
              `<Form>` — react-hook-form's `FormProvider` context does not
              extend past that `<Form>`'s own subtree, so the button cannot be
              lifted into a sibling `ModalFooter` without restructuring those
              two form components (out of scope here). */}
          <ModalHeader title={ACTION_LABEL[prompt.action]} onClose={() => setPrompt(null)} />
          <ModalBody>
          <div className="space-y-4" data-testid={`transition-prompt-${prompt.action}`}>
            {promptFor(prompt) === 'reason' ? (
              <CancelReasonForm pending={pending} onSubmit={(reason) => send(prompt, { reason })} />
            ) : (
              <RequestChangesNoteForm pending={pending} onSubmit={(note) => send(prompt, { note })} />
            )}
            <DialogActions
              size="sm"
              actions={[
                {
                  id: 'never-mind',
                  label: 'Never mind',
                  variant: 'ghost',
                  onClick: () => setPrompt(null),
                  'data-testid': 'transition-prompt-close',
                },
              ]}
            />
          </div>
          </ModalBody>
        </Modal>
      )}
    </div>
  );
}
